import React, { useState } from 'react';
import { useForm, useWatch, Controller } from 'react-hook-form';
import CitationImportSource from '../CitationImportSource/CitationImportSource';
import SelectedFileList from './components/SelectedFileList';
import { uploadFileToStorage } from './utils/uploadToS3';
import { createCitationImport } from './utils/createCitationImport';
import styles from './ImportFromFile.module.scss';
import { useReviewId } from 'query/hooks/useReviewId';
import FilePicker from 'components/shared/FilePicker/FilePicker';
import {
  Button,
  Link,
  Text,
  Select,
  Heading,
  Strong,
  Message,
  createToast,
  Toast,
  LoadingSpinner,
} from 'components/core';
import {
  Form,
  FormButtons,
  FormContainer,
  FormControl,
  FormLabel,
} from 'components/core/Form';
import { Stack, Inline } from 'components/layout';
import { PageContainer, PrimaryNavigationLayout } from 'components/shared';
import { PrimaryNavigationLayoutProps } from 'components/shared/PrimaryNavigation/PrimaryNavigationLayout';

type FormValues = {
  study_category: string;
  source: string;
  file: File | null;
};

type ImportCategory = {
  value: string;
  label: string;
};

export type StorageConfig = {
  url: string;
  formData: Record<string, string>;
};

type Props = {
  importCategories: ImportCategory[];
  disabledCategories: string[];
  storageConfig: StorageConfig;
  disabledOnDemo?: boolean;
} & PrimaryNavigationLayoutProps;

const MAX_FILE_SIZE_MB = 50;

const ImportFromFile: React.FC<Props> = ({
  importCategories,
  disabledCategories,
  storageConfig,
  disabledOnDemo = false,
  header,
  reviewer,
  subHeader,
}: Props) => {
  const reviewId = useReviewId();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      study_category: '',
      source: '',
      file: null,
    },
  });

  const selectedStudyCategory = useWatch({ control, name: 'study_category' });

  const onSubmit = async (data: FormValues) => {
    try {
      if (!reviewId || !data.file) {
        throw new Error('Could not import citations. Please try again.');
      }

      setIsSubmitting(true);

      // Step 1: Upload file to S3
      const s3Path = await uploadFileToStorage(data.file, storageConfig);

      // Step 2: Create citation import on backend
      const result = await createCitationImport({
        reviewId,
        s3Path,
        filename: data.file.name,
        studyCategory: data.study_category,
        citationImportSourceName: data.source || undefined,
      });

      // Step 3: Redirect on success
      if (result.success && result.redirect_url) {
        window.location.pathname = result.redirect_url;
      } else {
        const errorMessage =
          result.errors?.join(', ') ||
          'Could not import citations. Please try again.';
        throw new Error(errorMessage);
      }
    } catch (error) {
      setIsSubmitting(false);
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Could not import citations. Please try again.';
      createToast(
        <Toast type="danger" role="alert">
          {errorMessage}
        </Toast>
      );
    }
  };

  return (
    <PrimaryNavigationLayout
      header={header}
      subHeader={subHeader}
      reviewer={reviewer}
    >
      <PageContainer>
        <Stack spacing={5}>
          <Heading type="h3">Import from file</Heading>
          <Text size="sm">
            References can be imported using the EndNote XML format, the PubMed
            format, or the RIS text format.
            <Link
              to={'https://support.covidence.org/help/study-imports'}
              variant={'primary'}
              role={'link'}
              external
              className={styles.learnMoreLink}
            >
              Learn more
            </Link>
          </Text>
          {disabledOnDemo ? (
            <Message
              type="info"
              variant="prominent"
              dismissible={false}
              icon={false}
            >
              <Stack spacing={2}>
                <Text size="md" component="span">
                  <Strong>Imports are turned off for the demo</Strong>
                </Text>
                <Text size="sm" component="span">
                  This demo comes with sample data so you can explore how
                  Covidence works without uploading your own files.
                </Text>
              </Stack>
            </Message>
          ) : (
            <Form
              onSubmit={handleSubmit(onSubmit)}
              noValidate // Suppress browser default validations as we're using react-hook-form
            >
              <Stack spacing={5}>
                <FormContainer>
                  <Stack spacing={5}>
                    <Stack spacing={5}>
                      <FormControl error={errors.study_category?.message}>
                        <FormLabel size="sm">Import in to</FormLabel>
                        <Select
                          required
                          {...register('study_category', {
                            required: 'Please select option',
                          })}
                          disabled={importCategories.length === 0}
                        >
                          {!selectedStudyCategory && (
                            <option value="" disabled>
                              Select...
                            </option>
                          )}
                          {importCategories.map(
                            ({ value, label }: ImportCategory) => (
                              <option
                                key={value}
                                value={value}
                                disabled={disabledCategories.includes(value)}
                              >
                                {label}
                              </option>
                            )
                          )}
                        </Select>
                      </FormControl>

                      <Controller
                        name="source"
                        control={control}
                        render={({ field }) => (
                          <CitationImportSource
                            label="Source (Optional)"
                            value={field.value}
                            onChange={field.onChange}
                          />
                        )}
                      />
                    </Stack>

                    <Stack spacing={3}>
                      <Stack spacing={1}>
                        <Text size="sm" component="span">
                          <Strong>Upload file</Strong>
                        </Text>
                        <Text
                          size="sm"
                          variant="weak"
                          component="div"
                          id="file-upload-description"
                        >
                          You can upload 1 file at a time. File can be up to{' '}
                          {MAX_FILE_SIZE_MB}MB.
                        </Text>
                      </Stack>
                      <Controller
                        name="file"
                        control={control}
                        rules={{
                          required: 'Please choose a file to upload',
                        }}
                        render={({ field, fieldState }) => (
                          <>
                            {!field.value && (
                              <FilePicker
                                ref={field.ref}
                                required
                                acceptedFileTypes={['.xml', '.ris', '.txt']}
                                promptText="Drag and drop file to upload"
                                buttonText="Choose a file"
                                multiple={false}
                                onChange={(files) =>
                                  field.onChange(files[0] || null)
                                }
                                onBlur={field.onBlur}
                                maxFileSizeMB={MAX_FILE_SIZE_MB}
                                invalidFileTypeMessage="This file is not an accepted format. You can upload a file with XML, PubMed or RIS format"
                                aria-describedby="file-upload-description"
                                errorMessage={fieldState.error?.message || null}
                              />
                            )}
                            <div aria-live="polite">
                              {field.value && (
                                <SelectedFileList
                                  file={field.value}
                                  onRemove={() => field.onChange(null)}
                                />
                              )}
                            </div>
                          </>
                        )}
                      />
                    </Stack>
                  </Stack>
                </FormContainer>
                <FormButtons alignment="left">
                  <Button
                    size="md"
                    type="brand"
                    htmlType="submit"
                    disabled={isSubmitting}
                  >
                    Import
                  </Button>
                </FormButtons>
                <div aria-live="polite">
                  {isSubmitting && (
                    <Message
                      type="primary"
                      variant="prominent"
                      icon={false}
                      dismissible={false}
                    >
                      <Inline alignX="left" spacing={3}>
                        <LoadingSpinner size="lg" type="primary" />
                        <Text size="sm" component="span">
                          Importing citations. For large files this can take a
                          while.
                        </Text>
                      </Inline>
                    </Message>
                  )}
                </div>
              </Stack>
            </Form>
          )}
        </Stack>
      </PageContainer>
    </PrimaryNavigationLayout>
  );
};

export default ImportFromFile;
